import { useMemo, useState } from 'react';
import { Box, Button, Grid, InputAdornment, TextField } from '@mui/material';
import { OpenInNew as OpenInNewIcon, Search as SearchIcon } from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import { filter, some, toLower } from 'lodash';
import PropTypes from 'prop-types';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';

export const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const matchesSearch = (document, needle) =>
  some(
    [document.ndc, document.lotNumber, document.drugName, document.manufacturer, document.fileName],
    (value) => toLower(value ?? '').includes(needle)
  );

const OpenFileCell = ({ data }) => (
  <Button
    size='small'
    href={data.publicUrl}
    target='_blank'
    rel='noopener noreferrer'
    endIcon={<OpenInNewIcon fontSize='small' />}
    aria-label={`Open ${data.fileName}`}
  >
    Open
  </Button>
);

OpenFileCell.propTypes = {
  data: PropTypes.shape({
    fileName: PropTypes.string.isRequired,
    publicUrl: PropTypes.string.isRequired,
  }).isRequired,
};

const defaultColDef = { sortable: true, resizable: true };

const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: { suppressRowGroups: true, suppressValues: true, suppressPivotMode: true },
    },
    'filters-new',
  ],
};

const columnDefs = [
  { field: 'ndc', headerName: 'NDC', filter: 'agTextColumnFilter', minWidth: 130 },
  { field: 'lotNumber', headerName: 'Lot', filter: 'agTextColumnFilter', minWidth: 110 },
  {
    colId: 'drug',
    headerName: 'Drug',
    valueGetter: ({ data }) =>
      data.drugName ? `${data.drugName}${data.strength ? ` ${data.strength}` : ''}` : null,
    valueFormatter: ({ value }) => value ?? '—',
    filter: 'agTextColumnFilter',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'manufacturer',
    headerName: 'Manufacturer',
    valueFormatter: ({ value }) => value || '—',
    filter: 'agTextColumnFilter',
    flex: 1,
    minWidth: 150,
  },
  {
    // ISO timestamps sort chronologically as strings.
    field: 'uploadedAt',
    headerName: 'Uploaded',
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'MM/dd/yyyy h:mm a') : '—'),
    minWidth: 170,
  },
  {
    colId: 'actions',
    headerName: 'File',
    cellRenderer: OpenFileCell,
    sortable: false,
    resizable: false,
    width: 110,
    cellStyle: { textAlign: 'right' },
    headerClass: 'ag-right-aligned-header',
  },
];

const CoaDocumentsTable = ({ documents, loading }) => {
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const needle = toLower(search.trim());
    return needle ? filter(documents, (document) => matchesSearch(document, needle)) : documents;
  }, [documents, search]);

  if (loading) return <LoadingModule />;

  return (
    <Grid container direction='column' gap={2}>
      <Grid item>
        <TextField
          size='small'
          placeholder='Search by NDC, lot, drug or manufacturer'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            'aria-label': 'Search certificates of analysis',
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 360 }}
        />
      </Grid>

      {visible.length === 0 ? (
        // Wrapped in an item like ordersTable does: NothingFound's own container is height:100%,
        // which inside this column container would otherwise wrap into a second column next to
        // the search field and render off-centre.
        <Grid item>
          <NothingFound
            customMessage={
              documents.length === 0
                ? 'No certificates of analysis have been uploaded yet'
                : 'No certificates match your search'
            }
          />
        </Grid>
      ) : (
        // A fixed-height grid scrolls (and virtualizes) its own rows, so the page doesn't grow
        // with every certificate.
        <Box sx={{ height: 'calc(100vh - 260px)', minHeight: 400 }}>
          <AgGridReact
            rowData={visible}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={({ data }) => data.fileKey}
            sideBar={sideBar}
            enableFilterHandlers
          />
        </Box>
      )}
    </Grid>
  );
};

CoaDocumentsTable.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      ndc: PropTypes.string.isRequired,
      lotNumber: PropTypes.string.isRequired,
      fileKey: PropTypes.string.isRequired,
      fileName: PropTypes.string.isRequired,
      publicUrl: PropTypes.string.isRequired,
      sizeBytes: PropTypes.number,
      uploadedAt: PropTypes.string,
      drugName: PropTypes.string,
      strength: PropTypes.string,
      manufacturer: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
};

CoaDocumentsTable.defaultProps = {
  loading: false,
};

export default CoaDocumentsTable;
